import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

import CardDialog from '~/components/CardDialog';
import WithConfirm from '~/components/ConfirmModal';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import Editor from '~/components/core/Editor';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import AddIcon from '~/components/icons/AddIcon';
import InlineIconButton from '~/components/InlineIconButton';
import PlainTable from '~/components/PlainTable';
import EmailConfigurationBuilderContainer from '~/components/SystemConfiguration/EmailConfiguration/EmailConfigurationBuilderContainer';
import EmailConfigurationPreviewContainerCardDialogWrapper from '~/components/SystemConfiguration/EmailConfiguration/EmailConfigurationPreviewContainer';
import type {
  EmailConfigurationColumn,
  EmailConfigurationData,
  EmailConfigurationSubmitValues,
  EmailSignatureAndTitleConfigurationProps,
  ExistingEmailConfigurationData,
} from '~/components/SystemConfiguration/EmailConfiguration/emailConfigurationTypes';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import { serverDateToLocal } from '~/DateTimeUtils';
import colors from '~/theme/tailwind/colors';
import { reportAxiosError, subOrgIdToNameDict } from '~/Utils';

import CopyFileIcon from '../../icons/CopyFileIcon';
import EyeIcon from '../../icons/EyeIcon';
import PencilIcon from '../../icons/PencilIcon';
import TrashIcon from '../../icons/TrashIcon';

const BASE_PATH = '/api/v1/email_configuration/organizations';

interface EmailDomain {
  id: number;
  domain: string;
}

const defaultEmailConfigurationLoader = () => {
  return <SkeletonTable rowsCount={2} columnsCount={6} />;
};

const subOrgEmailConfigurationLoader = () => {
  return <SkeletonTable rowsCount={3} columnsCount={6} />;
};

const EmailSignatureAndTitleConfiguration: React.FC<EmailSignatureAndTitleConfigurationProps> = ({
  isErrorEmailConfigurationTableData,
  isLoadingEmailConfigurationTableData,
  emailSignatureAndTitleTableData,
  reloadData,
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const subOrganizationsNames: { [key: number]: string } = subOrgIdToNameDict(organization);
  const subOrganizationIds = Object.keys(subOrganizationsNames).map(Number);

  // Due to Editors' tight coupling with their initial content, direct updates don't trigger re-renders. By updating the component's key via useEffect,
  // we force a rerender to reflect content changes, ensuring the UI stays updated
  useEffect(() => {
    const key = uuidv4();
    setEmailTitleEditorKey(key);
  }, [emailSignatureAndTitleTableData]);

  const emailDomainsUrl = `/api/v1/organizations/${organization.id}/email_domains`;

  const {
    data: emailDomains,
    isLoading: isLoadingEmailDomains,
    isError: isErrorEmailDomains,
  } = useDataFetcher<EmailDomain[]>(emailDomainsUrl);

  const [emailTitleEditorKey, setEmailTitleEditorKey] = useState<string>(uuidv4());
  const [isAddSubOrg, setIsAddSubOrg] = useState<boolean>(false);
  const [emailConfigurationPreviewData, setEmailConfigurationPreviewData] = useState<
    EmailConfigurationData | undefined
  >();
  const [emailConfigurationDataToEdit, setEmailConfigurationDataToEdit] = useState<
    EmailConfigurationData | undefined
  >();
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const handleAddSubOrgConfiguration = () => {
    setIsAddSubOrg(true);
  };

  const handlePreview = (configurationData: EmailConfigurationData) => {
    setEmailConfigurationPreviewData(configurationData);
  };

  const handleClosePreview = () => {
    setEmailConfigurationPreviewData(undefined);
  };

  const handleCopy = (configurationData: EmailConfigurationData) => {
    setIsAddSubOrg(true);
    setEmailConfigurationDataToEdit({ ...configurationData, id: undefined, sub_organization_id: undefined });
  };

  const handleEditConfiguration = (configurationData: EmailConfigurationData) => {
    setEmailConfigurationDataToEdit(configurationData);
  };

  const handleCloseEditConfigurationDialog = () => {
    setIsAddSubOrg(false);
    setEmailConfigurationDataToEdit(undefined);
  };

  const handleAddEmailConfiguration = async (values: EmailConfigurationSubmitValues) => {
    try {
      await axios.post(`${BASE_PATH}/${organization.id}`, values);
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  const handleUpdateEmailConfiguration = async (
    values: EmailConfigurationSubmitValues,
    configurationId: number | undefined
  ) => {
    if (!configurationId) return;
    try {
      await axios.patch(`${BASE_PATH}/${organization.id}/${configurationId}`, values);
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  const handleConfigurationDelete = async (emailConfigurationId: number) => {
    setIsUpdating(true);

    try {
      await axios.delete(`${BASE_PATH}/${organization.id}/${emailConfigurationId}`);
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsUpdating(false);
    }
    await reloadData();
  };

  const defaultEmailConfigurationsData = emailSignatureAndTitleTableData?.filter(
    (row: EmailConfigurationData) => !row.sub_organization_id
  );

  const subOrgsEmailConfigurationData = emailSignatureAndTitleTableData?.filter(
    (row: EmailConfigurationData) => row.sub_organization_id
  );

  const subOrgsWithEmailConfigurationIds: (number | undefined)[] =
    emailSignatureAndTitleTableData
      ?.filter((row: EmailConfigurationData) => row.sub_organization_id)
      .map((subOrgConfig: EmailConfigurationData) => subOrgConfig.sub_organization_id) || [];

  const hasEmailConfigForAllSubOrgs =
    subOrgsWithEmailConfigurationIds.length === Object.keys(subOrganizationsNames).length;

  const emailConfigurationsColumns = [
    {
      id: 'edited_by_username',
      label: 'Edited By',
    },
    {
      id: 'edit_date',
      label: 'Edit Date',
      specialCell: (rowConfigurationData: ExistingEmailConfigurationData) =>
        rowConfigurationData.edited_at ? serverDateToLocal(rowConfigurationData.edited_at) : '',
    },
    {
      id: 'email_default_title',
      label: 'Default Email Subject',
      specialCell: ({ email_default_title }: Pick<ExistingEmailConfigurationData, 'email_default_title'>) => {
        return (
          <Editor key={emailTitleEditorKey} disabled content={email_default_title} containerClassName="border-0" />
        );
      },
    },
    {
      id: 'email_signature',
      label: 'Email Signature',
      specialCell: (rowConfigurationData: ExistingEmailConfigurationData) => {
        return (
          <Button color="primary" disabled={false} onClick={() => handlePreview(rowConfigurationData)}>
            <EyeIcon iconColor={colors.teal['700']} className="mr-4" />
            Generate example
          </Button>
        );
      },
    },
  ];

  const getRelevantDefaultEmailConfigurationsColumns = (): EmailConfigurationColumn => {
    return organization.is_suborgs_domains_enabled
      ? {
          id: 'emailDomain',
          label: 'Email Domain',
          specialCell: (rowConfigurationData: ExistingEmailConfigurationData) => {
            if (emailDomains) {
              return emailDomains.find(
                (domain: { id: number; domain: string }) => domain.id === rowConfigurationData.email_domain_id
              )?.domain;
            }
          },
        }
      : {
          id: 'organization',
          label: 'Organization',
          specialCell: () => organization.name,
        };
  };

  const defaultEmailConfigurationsColumns = [
    getRelevantDefaultEmailConfigurationsColumns(),
    ...emailConfigurationsColumns,
    {
      id: 'actions',
      label: 'Actions',
      specialCell: (rowConfigurationData: ExistingEmailConfigurationData) => {
        return (
          <InlineIconButton
            icon={PencilIcon}
            onClick={() => handleEditConfiguration(rowConfigurationData)}
            tooltipTitle="Edit"
            defaultColor={undefined}
            disabled={undefined}
            className="m-4 hover:text-teal-700"
            iconColor="currentColor"
            iconStyle={undefined}
            useIconButton={undefined}
            ignorePermissions={undefined}
            ariaLabel={undefined}
          />
        );
      },
    },
  ];

  const subOrgEmailConfigurationsColumns = [
    {
      id: 'sub_organization_id',
      label: 'Sub-Org',
      specialCell: (rowConfigurationData: ExistingEmailConfigurationData) =>
        rowConfigurationData.sub_organization_id
          ? subOrganizationsNames[rowConfigurationData.sub_organization_id]
          : null,
    },
    ...emailConfigurationsColumns,
    {
      id: 'actions',
      label: 'Actions',
      specialCell: (rowConfigurationData: ExistingEmailConfigurationData) => {
        const subOrgName = rowConfigurationData.sub_organization_id
          ? subOrganizationsNames[rowConfigurationData.sub_organization_id]
          : null;
        return (
          <div>
            <InlineIconButton
              className="m-4 hover:text-teal-700"
              iconColor="currentColor"
              icon={PencilIcon}
              onClick={() => handleEditConfiguration(rowConfigurationData)}
              tooltipTitle="Edit"
              defaultColor={undefined}
              disabled={undefined}
              iconStyle={undefined}
              useIconButton={undefined}
              ignorePermissions={undefined}
              ariaLabel={undefined}
            />
            <WithConfirm
              title="Delete sub organization configuration?"
              contentText={`Are you sure you want to delete ${subOrgName} email configuration? This will cause emails sent to use the default organization level configuration`}
              primaryButtonName="Delete"
            >
              <InlineIconButton
                className="m-4 hover:text-teal-700"
                iconColor="currentColor"
                icon={TrashIcon}
                onClick={() => handleConfigurationDelete(rowConfigurationData.id)}
                tooltipTitle="Delete"
                iconStyle={undefined}
                useIconButton={undefined}
                ariaLabel={undefined}
                defaultColor={undefined}
                disabled={isUpdating}
                ignorePermissions={undefined}
              />
            </WithConfirm>
            <InlineIconButton
              className="m-4 hover:text-teal-700"
              iconColor="currentColor"
              icon={CopyFileIcon}
              onClick={() => handleCopy(rowConfigurationData)}
              tooltipTitle="Copy"
              defaultColor={undefined}
              disabled={hasEmailConfigForAllSubOrgs}
              iconStyle={undefined}
              useIconButton={undefined}
              ignorePermissions={undefined}
              ariaLabel={undefined}
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="m-20">
        <LoadingSwitch
          isLoading={isLoadingEmailDomains || isLoadingEmailConfigurationTableData}
          isError={isErrorEmailDomains || isErrorEmailConfigurationTableData}
          LoadingComponent={defaultEmailConfigurationLoader}
        >
          <CardDialog title="Default Email Configuration">
            <PlainTable columns={defaultEmailConfigurationsColumns} rows={defaultEmailConfigurationsData} />
          </CardDialog>
        </LoadingSwitch>
      </div>

      {organization.sub_organizations_enabled && (
        <div className="m-20">
          <LoadingSwitch
            isLoading={isLoadingEmailConfigurationTableData}
            isError={isErrorEmailConfigurationTableData}
            LoadingComponent={subOrgEmailConfigurationLoader}
          >
            <CardDialog
              title="Sub-Org Email Configuration"
              action={
                <div className="p-4">
                  <Button
                    color="primary"
                    disabled={hasEmailConfigForAllSubOrgs}
                    onClick={handleAddSubOrgConfiguration}
                    startIcon={<AddIcon iconColor="currentColor" />}
                  >
                    Add signature
                  </Button>
                </div>
              }
            >
              <>
                <div className="pb-20">
                  <AlertBanner
                    alertType={AlertBanner.ALERT_TYPES.WARNING}
                    note={`Please note: Emails which are not attached to a claim (i.e. not attached to a sub-organization) will present the default ${
                      organization.is_suborgs_domains_enabled ? 'domain' : 'organization'
                    } signature`}
                    withIcon
                  />
                </div>
                <PlainTable columns={subOrgEmailConfigurationsColumns} rows={subOrgsEmailConfigurationData} />
              </>
            </CardDialog>
          </LoadingSwitch>
        </div>
      )}

      {emailConfigurationDataToEdit || isAddSubOrg ? (
        <EmailConfigurationBuilderContainer
          reloadTable={reloadData}
          handleConfigSubmit={isAddSubOrg ? handleAddEmailConfiguration : handleUpdateEmailConfiguration}
          handleClose={handleCloseEditConfigurationDialog}
          emailConfigurationData={emailConfigurationDataToEdit}
          isAddSubOrg={isAddSubOrg}
          excludedSubOrgsIdsForSelect={subOrgsWithEmailConfigurationIds}
        />
      ) : null}

      {emailConfigurationPreviewData ? (
        <EmailConfigurationPreviewContainerCardDialogWrapper
          onClose={handleClosePreview}
          emailConfiguration={emailConfigurationPreviewData}
          organizationSubOrgIds={subOrganizationIds}
        />
      ) : null}
    </>
  );
};

export default EmailSignatureAndTitleConfiguration;
